import { cn } from "../../../lib/cn";
import { contentTooltipSizeClass } from "./content-tooltip-sizes";
import type {
	ContentTooltipColorScheme,
	ContentTooltipSurface,
	ResolveContentTooltipChromeInput,
} from "./content-tooltip-types";

/**
 * Forced dark glass — ability/completion SoT (`ABILITY_DARK` bag).
 * Radius via size token (`rounded-md`); keep #0f1320 fill.
 */
export const CONTENT_TOOLTIP_DARK_SURFACE_CLASS =
	"border border-white/15 bg-[#0f1320]/95 text-white shadow-[0_18px_45px_rgba(0,0,0,0.55)] backdrop-blur-sm";

/**
 * Forced light glass (blurred), independent of app theme — light twin of dark SoT.
 */
export const CONTENT_TOOLTIP_LIGHT_SURFACE_CLASS =
	"border border-black/10 bg-white/85 text-zinc-900 shadow-[0_18px_45px_rgba(0,0,0,0.18)] backdrop-blur-sm";

/**
 * Theme-paired default chrome — light SoT in light mode, ability dark SoT in dark mode.
 * Used by default ContentTooltip + SHORT / HUD bags.
 */
export const CONTENT_TOOLTIP_SYSTEM_SURFACE_CLASS = cn(
	CONTENT_TOOLTIP_LIGHT_SURFACE_CLASS,
	"dark:border-white/15 dark:bg-[#0f1320]/95 dark:text-white dark:shadow-[0_18px_45px_rgba(0,0,0,0.55)]",
);

/**
 * Invert / contrast-of-resolved (local override).
 * Light resolved → dark plate; dark resolved → light plate.
 * COLOR_MODES FadedSquares `contrast` semantics — not a11y high-contrast.
 */
export const CONTENT_TOOLTIP_INVERT_SURFACE_CLASS =
	"border border-transparent bg-foreground/95 text-background shadow-md backdrop-blur-sm dark:bg-background/95 dark:text-foreground dark:border-white/10";

/**
 * Structural inverse chip (skill morph). Same paint family as invert.
 */
export const CONTENT_TOOLTIP_INVERSE_SURFACE_CLASS =
	"border-0 bg-foreground/95 text-background shadow-md backdrop-blur-sm dark:bg-background/95 dark:text-foreground";

/**
 * Loot / Prefer glass.
 */
export const CONTENT_TOOLTIP_GLASS_SURFACE_CLASS =
	"border-0 bg-white/70 text-foreground shadow-xl backdrop-blur-sm dark:bg-black/45 dark:text-white";

function paletteClass(colorScheme: ContentTooltipColorScheme): string {
	switch (colorScheme) {
		case "invert":
			return CONTENT_TOOLTIP_INVERT_SURFACE_CLASS;
		case "dark":
			return CONTENT_TOOLTIP_DARK_SURFACE_CLASS;
		case "light":
			return CONTENT_TOOLTIP_LIGHT_SURFACE_CLASS;
		case "system":
			return CONTENT_TOOLTIP_SYSTEM_SURFACE_CLASS;
	}
}

function surfaceOverride(
	surface: ContentTooltipSurface,
	colorScheme: ContentTooltipColorScheme,
): string | null {
	switch (surface) {
		case "default":
			return null;
		case "inverse":
			// Structural morph chrome — invert palette when not forced light/dark.
			if (colorScheme === "dark" || colorScheme === "light") {
				return null;
			}
			return CONTENT_TOOLTIP_INVERSE_SURFACE_CLASS;
		case "glass":
			if (colorScheme === "system" || colorScheme === "invert") {
				return CONTENT_TOOLTIP_GLASS_SURFACE_CLASS;
			}
			// Forced light/dark: keep palette; glass only tweaks shadow.
			return "shadow-xl";
	}
}

/**
 * Arrow fill classes matching panel for each colorScheme.
 * Prefer {@link resolveContentTooltipArrowFills} for stacked border+face arrows.
 */
export function contentTooltipArrowFillClass(
	colorScheme: ContentTooltipColorScheme,
): string {
	switch (colorScheme) {
		case "invert":
			return "bg-foreground dark:bg-background";
		case "dark":
			return "bg-[#0f1320]";
		case "light":
			return "bg-white";
		case "system":
			return "bg-white/85 dark:bg-[#0f1320]";
	}
}

/**
 * Composable chrome map: `(surface, colorScheme, size) → className`.
 * All schemes include backdrop blur; default radius via size token (`rounded-md`).
 */
export function resolveContentTooltipChrome({
	surface,
	colorScheme,
	size,
}: ResolveContentTooltipChromeInput): string {
	const override = surfaceOverride(surface, colorScheme);
	return cn(
		override ?? paletteClass(colorScheme),
		override != null &&
			colorScheme !== "system" &&
			colorScheme !== "invert" &&
			surface === "glass"
			? paletteClass(colorScheme)
			: null,
		override != null &&
			(colorScheme === "dark" || colorScheme === "light") &&
			surface === "inverse"
			? paletteClass(colorScheme)
			: null,
		contentTooltipSizeClass(size),
	);
}
